import clsx from 'clsx';
import { ArrowDown, ArrowUp, Paperclip, X } from 'lucide-react';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMarkRead, useMessages, useSendMessage, useUpload, useWorkspace } from '@/api';
import { useStudioReply } from '@/api/studio';
import type { Message, Person } from '@/api/types';
import { errorMessage } from '@/lib/errors';
import { dayKey, formatDayHeading, formatTime } from '@/lib/format';
import { ALLOWED_EXTENSIONS, precheck } from '@/lib/upload';
import { AttachmentChip } from './AttachmentChip';
import { ErrorState } from './QueryState';
import { PersonLink } from './PersonDialog';
import { Avatar } from './ui';

const GROUP_GAP_MS = 10 * 60_000;

/** A phone or tablet keyboard: there Enter is a new line and the arrow button sends. */
const touchKeyboard = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

/** A file picked for the message: uploaded at once, attached by id on send. */
type Picked = { key: number; name: string; status: 'uploading' | 'done' | 'error'; id?: string; sizeKb?: number; error?: string };

/**
 * A conversation bound to a project or a task. Built for long histories:
 * loads the newest page first, earlier pages on demand, groups consecutive
 * messages of one author, separates days and marks where unread begins.
 */
export function Thread({
  threadId,
  projectId,
  unread = 0,
  placeholder = 'Написать команде',
  className,
  as,
}: {
  threadId: string;
  /** The project the files of this conversation are filed under. */
  projectId?: string;
  unread?: number;
  placeholder?: string;
  className?: string;
  /** Studio mode: write as this team member; the client's read state is left alone. */
  as?: Person;
}) {
  const { data: ws } = useWorkspace();
  const q = useMessages(threadId, !!as);
  const clientSend = useSendMessage(threadId);
  const studioSend = useStudioReply(threadId);
  const pending = clientSend.isPending || studioSend.isPending;
  const me = as ?? ws?.me;
  const markRead = useMarkRead();
  const [draft, setDraft] = useState('');
  const [picked, setPicked] = useState<Picked[]>([]);
  const pickSeq = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const upload = useUpload();
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  // Freeze the unread count at open, so the divider stays while reading.
  const [unreadAtOpen] = useState(unread);

  const items = useMemo(() => (q.data ? [...q.data.pages].reverse().flatMap((p) => p.items) : []), [q.data]);
  const total = q.data?.pages[0]?.total ?? 0;
  const firstUnreadId = unreadAtOpen > 0 ? items.filter((m) => m.author.side === (as ? 'client' : 'studio')).at(-unreadAtOpen)?.id : undefined;

  useEffect(() => {
    if (!as) markRead.mutate(threadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Keep the view pinned to the latest message unless the reader scrolled up.
  const lastId = items.at(-1)?.id;
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [lastId]);

  // Loading an earlier page must not throw the reader to another place.
  const heightBefore = useRef(0);
  const loadEarlier = () => {
    heightBefore.current = scroller.current?.scrollHeight ?? 0;
    stick.current = false;
    q.fetchNextPage();
  };
  const firstId = items[0]?.id;
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && heightBefore.current) {
      el.scrollTop += el.scrollHeight - heightBefore.current;
      heightBefore.current = 0;
    }
  }, [firstId]);

  const uploading = picked.some((p) => p.status === 'uploading');
  const ready = picked.filter((p) => p.status === 'done' && p.id);

  async function pick(files: File[]) {
    const queued = files.map((f) => ({ f, key: ++pickSeq.current, problem: precheck(f) }));
    setPicked((all) => [...all, ...queued.map(({ f, key, problem }): Picked => (problem ? { key, name: f.name, status: 'error', error: problem } : { key, name: f.name, status: 'uploading' }))]);
    const set = (key: number, patch: Partial<Picked>) => setPicked((all) => all.map((x) => (x.key === key ? { ...x, ...patch } : x)));
    for (const { f, key, problem } of queued) {
      if (problem) continue;
      try {
        const sizeKb = Math.max(1, Math.round(f.size / 1024));
        const d = await upload.mutateAsync({ projectId, name: f.name, sizeKb, file: f });
        set(key, { status: 'done', id: d.id, sizeKb: d.sizeKb });
      } catch (e) {
        set(key, { status: 'error', error: errorMessage(e) });
      }
    }
  }

  const submit = () => {
    const body = draft.trim();
    if ((!body && ready.length === 0) || pending || uploading) return;
    stick.current = true;
    // The draft stays until the server has the message: a failed send must not eat the text.
    const sent = {
      onSuccess: () => {
        setDraft((d) => (d.trim() === body ? '' : d));
        setPicked((all) => all.filter((p) => !ready.some((r) => r.key === p.key)));
      },
    };
    if (as) studioSend.mutate({ body, authorId: as.id }, sent);
    else clientSend.mutate({ body, documentIds: ready.map((r) => r.id!) }, sent);
  };
  const sendFailed = clientSend.isError || studioSend.isError;

  return (
    <div className={clsx('flex min-h-0 flex-col', className)}>
      <div
        ref={scroller}
        className="relative min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-1"
        onScroll={(e) => {
          const el = e.currentTarget;
          const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          stick.current = bottom;
          setAtBottom(bottom);
        }}
      >
        {q.isLoading && <p className="py-6 text-sm text-muted">Загружаем переписку…</p>}
        {q.isError && !q.data && (
          <div className="py-6">
            <ErrorState error={q.error} compact retrying={q.isFetching} onRetry={() => q.refetch()} />
          </div>
        )}
        {q.hasNextPage && (
          <div className="py-4 text-center">
            <button className="btn-secondary" onClick={loadEarlier} disabled={q.isFetchingNextPage}>
              {q.isFetchingNextPage ? 'Загружаем…' : `Показать раньше, ещё ${total - items.length}`}
            </button>
          </div>
        )}
        {!q.isLoading && !q.isError && items.length === 0 && <p className="py-10 text-center text-sm text-muted">Здесь пока тихо. Напишите — команда ответит здесь же.</p>}

        {items.map((m, i) => {
          const prev = items[i - 1];
          const newDay = !prev || dayKey(prev.at) !== dayKey(m.at);
          const grouped = !newDay && prev && prev.author.id === m.author.id && new Date(m.at).getTime() - new Date(prev.at).getTime() < GROUP_GAP_MS && m.id !== firstUnreadId;
          return (
            <Fragment key={m.id}>
              {newDay && (
                <div className="flex justify-center py-2">
                  {/* Not sticky: several day chips stuck at the same top overlapped («сре Вчера бря»). */}
                  <span className="rounded-full bg-canvas px-2.5 py-0.5 text-xs text-muted shadow-hair">{formatDayHeading(m.at)}</span>
                </div>
              )}
              {m.id === firstUnreadId && (
                <div className="my-3 flex items-center gap-3 text-xs font-medium text-ink" role="separator">
                  <span className="h-px flex-1 bg-ink" />
                  Новые сообщения
                  <span className="h-px flex-1 bg-ink" />
                </div>
              )}
              <MessageRow m={m} mine={m.author.id === me?.id} grouped={!!grouped} />
            </Fragment>
          );
        })}

        {!atBottom && (
          <button
            onClick={() => {
              stick.current = true;
              scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
            }}
            className="btn-secondary sticky bottom-2 left-full z-10 ml-auto flex shadow-pop"
          >
            <ArrowDown className="size-4" /> К последним
          </button>
        )}
      </div>

      <form
        className="border-t border-line px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {sendFailed && (
          <p role="alert" className="mb-2 flex items-center gap-2 text-sm text-overdue">
            Не отправлено: текст сохранён в поле.
            <button type="button" className="underline" onClick={submit} disabled={pending}>
              Повторить
            </button>
          </p>
        )}
        {picked.length > 0 && (
          <ul className="mb-2 flex flex-wrap gap-2 text-sm" aria-label="Файлы к сообщению">
            {picked.map((p) => (
              <li key={p.key} className="inline-flex max-w-full items-center gap-2 rounded bg-panel px-2.5 py-1 shadow-hair" data-status={p.status}>
                <Paperclip className="size-3.5 shrink-0 text-muted" />
                <span className="truncate">{p.name}</span>
                {p.status === 'uploading' && <span className="text-muted">загружается…</span>}
                {p.status === 'error' && (
                  <span className="text-overdue" role="alert">
                    не загружен: {p.error}
                  </span>
                )}
                <button type="button" className="text-muted hover:text-ink" aria-label={`Убрать ${p.name}`} onClick={() => setPicked((all) => all.filter((x) => x.key !== p.key))}>
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-end gap-2 rounded bg-canvas p-1.5 shadow-hair focus-within:ring-2 focus-within:ring-ink/80">
          {!as && (
            <>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ALLOWED_EXTENSIONS.map((x) => `.${x}`).join(',')}
                className="hidden"
                data-testid="attach-input"
                onChange={(e) => {
                  const files = [...(e.target.files ?? [])];
                  e.target.value = '';
                  if (files.length) void pick(files);
                }}
              />
              <button type="button" className="btn-ghost size-8 shrink-0 px-0" aria-label="Приложить файл" onClick={() => fileInput.current?.click()}>
                <Paperclip className="size-4" />
              </button>
            </>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter is a new line; on a touch keyboard Enter is always a new line.
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !touchKeyboard()) {
                e.preventDefault();
                submit();
              }
            }}
            rows={1}
            placeholder={placeholder}
            aria-label="Сообщение"
            enterKeyHint={touchKeyboard() ? 'enter' : 'send'}
            className="max-h-40 min-h-8 flex-1 resize-none bg-transparent py-1.5 text-base placeholder:text-faint focus:outline-none"
          />
          <button type="submit" className="btn-primary size-8 shrink-0 px-0" disabled={(!draft.trim() && ready.length === 0) || pending || uploading} aria-label="Отправить">
            <ArrowUp className="size-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

function MessageRow({ m, mine, grouped }: { m: Message; mine: boolean; grouped: boolean }) {
  return (
    <article className={clsx('group flex gap-3', grouped ? 'mt-0.5' : 'mt-4')}>
      <div className="w-8 shrink-0">
        {!grouped &&
          (mine ? (
            <Avatar person={m.author} />
          ) : (
            <PersonLink person={m.author}>
              <Avatar person={m.author} />
            </PersonLink>
          ))}
      </div>
      <div className="min-w-0 flex-1">
        {!grouped && (
          <header className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium">{mine ? 'Вы' : <PersonLink person={m.author} />}</span>
            {!mine && m.author.side === 'studio' && <span className="text-xs text-muted">{m.author.role}</span>}
            <time className="text-xs text-faint" dateTime={m.at}>
              {formatTime(m.at)}
            </time>
          </header>
        )}
        <div className="relative">
          {grouped && (
            <time className="absolute -left-11 top-0.5 hidden w-8 text-right text-[10px] text-faint group-hover:block" dateTime={m.at}>
              {formatTime(m.at)}
            </time>
          )}
          <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>
        </div>
        {m.attachments.map((a) => (
          <AttachmentChip key={a.id || a.name} a={a} icon={<Paperclip className="size-3.5 text-muted" />} className="mt-2 inline-flex items-center gap-2 rounded bg-panel px-2.5 py-1.5 text-sm shadow-hair" />
        ))}
      </div>
    </article>
  );
}
